"use client";

import { useContext, useState } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";
import { createPath } from "@/service/routing/routing";
import { clientGet } from "@/service/client-api/client-api";
import { BookmarkIcon, ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { toast } from "sonner";

export const FavouriteManager = ({
  resourceType,
  resourceId,
  isFavourite,
  favouritePath,
  simple = false,
  label,
  className,
}: {
  resourceType:
    | "medical_facility_unit"
    | "medical_facility"
    | "publication"
    | "vademecum"
    | "calculator"
    | "med"
    | "medical_supply"
    | "tutorial"
    | "fast_question"
    | "calculator_category";
  resourceId: number | string;
  isFavourite: boolean;
  favouritePath?: string;
  simple?: boolean;
  label?: string;
  className?: string;
}) => {
  const [visibility, setVisible] = useState(false);
  const [favourite, setFavourite] = useState(isFavourite);
  const { logView } = useContext(ResourceContext);
  const ref = useDetectClickOutside({
    onTriggered: () => {
      setVisible(false);
    },
  });

  const onClick = async () => {
    await clientGet("favourite", {
      resourceType,
      resourceId,
      action: favourite ? "remove" : "add",
    });

    logEvent("toggle_favourite_resource", logView, {
      resource_type: resourceType,
      resource_id: resourceId,
      resource_action: favourite ? "remove" : "add",
    });

    toast.info(favourite ? "Usunięto z zapisanych" : "Dodano do zapisanych");

    setFavourite(!favourite);
  };

  if (label) {
    return (
      <Button
        variant={"outline"}
        size={"sm"}
        onClick={onClick}
        className={cn("w-full", favourite && "border-remedium-blue", className)}
      >
        <BookmarkIcon
          size={"16px"}
          className={cn(
            favourite &&
              "[&_path]:stroke-remedium-blue [&_path]:fill-remedium-blue",
          )}
        />
        {label}
      </Button>
    );
  }

  if (simple) {
    return (
      <Button
        variant={"outline"}
        onClick={onClick}
        className={cn(
          "w-10 hover:scale-105 active:scale-95",
          favourite && "border-remedium-blue",
        )}
      >
        <BookmarkIcon
          size={"16px"}
          className={cn(
            favourite &&
              "[&_path]:stroke-remedium-blue [&_path]:fill-remedium-blue",
          )}
        />
      </Button>
    );
  }

  const optionStyles = `first:mb-2 block mx-auto py-[7.5px] px-2 rounded-md text-[#333] text-sm not-italic font-normal leading-[17px] !w-[142px] hover:text-remedium-blue hover:bg-[#F9FAFC]`;

  return (
    <div
      className={cn("relative", {
        active: favourite,
      })}
    >
      <div
        className={"hover:cursor-pointer"}
        onClick={() => {
          setVisible(true);
        }}
      >
        <div
          className={cn(
            "inline-flex h-9 items-center justify-center gap-2 rounded-lg border-[1.33px] border-zinc-200 py-2 pr-2 pl-2.5",
            favourite && "border-remedium-blue",
          )}
        >
          <BookmarkIcon
            size={"20px"}
            className={
              favourite
                ? "[&_path]:stroke-remedium-blue [&_path]:fill-remedium-blue"
                : "stroke-zinc-500"
            }
          />
          <ChevronDownIcon size={"20px"} className={"stroke-zinc-500"} />
        </div>
      </div>
      <div
        className={cn(
          `pointer-events-none absolute top-[42px] right-0 z-[1] h-[88px] w-[170px] rounded-[var(--12,12px)] border border-[#DEDEE5] bg-white px-[10px] py-2 text-center opacity-0 shadow-[0px_4px_42px_0px_rgba(0,0,0,0.05)]`,
          visibility && "pointer-events-auto opacity-100",
        )}
        ref={ref}
      >
        <div
          className={cn(
            optionStyles,
            favourite && "text-remedium-blue bg-[#F9FAFC]",
          )}
          onClick={onClick}
        >
          {favourite ? "Usuń z zapisanych" : "Zapisz"}
        </div>
        <a className={optionStyles} href={createPath(favouritePath)}>
          Zapisane
        </a>
      </div>
    </div>
  );
};
